/**
 * <SupportWorkspace> — the support team's whole module on one screen: the
 * board, releases, versioning and the autonomy settings, as tabs.
 *
 * Mounted wherever the team works: the Run view's synthetic "Support" menu
 * entry, the builder workbench's Support tab, and the downloaded runtime when
 * it is pointed at the platform. A tab whose transport method the surface does
 * not provide is not offered. Somebody who is NOT on the team (a reporter who
 * reached the screen through a link) gets their own issues instead of a board
 * they cannot use.
 */

import { useCallback, useEffect, useState } from 'react'
import { GitBranch, LayoutGrid, Plus, Rocket, Settings2 } from 'lucide-react'

import type { SupportMeta, SupportNotify, SupportRuntime } from './contract'
import IssueDetail from './IssueDetail'
import MyIssues from './MyIssues'
import PolicyEditor from './PolicyEditor'
import ReleasesView from './ReleasesView'
import ReportIssueDialog from './ReportIssueDialog'
import SupportBoard from './SupportBoard'
import VersioningPanel from './VersioningPanel'
import { ErrorLine, Spinner, SupportScope, Tabs, btn, errorText } from './ui'

export type SupportTab = 'board' | 'releases' | 'versioning' | 'settings'

const TABS: SupportTab[] = ['board', 'releases', 'versioning', 'settings']

/** A tab id from a URL, or undefined when it names none. */
export function supportTabOf(raw: unknown): SupportTab | undefined {
  return TABS.includes(raw as SupportTab) ? raw as SupportTab : undefined
}

export interface SupportWorkspaceProps {
  runtime: SupportRuntime
  /** The meta the host already holds; fetched here when absent. */
  meta?: SupportMeta | null
  notify?: SupportNotify
  /** QC's "Open staging" — the host opens the app on that fix's staging copy. */
  onOpenStaging?: (issueNumber: number) => void
  /** Open this issue on mount (deep link / notification). */
  initialIssue?: number | null
  /** Reports the open issue so the host can keep it in its URL. */
  onIssueChange?: (issueNumber: number | null) => void
  initialTab?: SupportTab
  /** The `source` stamped on issues filed from here (`builder` in the workbench). */
  newIssueSource?: string
  getContext?: () => Record<string, unknown>
  /** Fill the host's height (the Run view pane, the workbench page). */
  fill?: boolean
}

export default function SupportWorkspace({
  runtime, meta: metaProp, notify, onOpenStaging, initialIssue, onIssueChange,
  initialTab = 'board', newIssueSource = 'run', getContext, fill = true,
}: SupportWorkspaceProps) {
  const [meta, setMeta] = useState<SupportMeta | null>(metaProp || null)
  const [error, setError] = useState('')
  const [tab, setTab] = useState<SupportTab>(initialIssue ? 'board' : initialTab)
  const [boardIssue, setBoardIssue] = useState<number | null>(initialIssue || null)
  const [mineIssue, setMineIssue] = useState<number | null>(initialIssue || null)
  const [reporting, setReporting] = useState(false)
  const [listKey, setListKey] = useState(0)

  useEffect(() => { if (metaProp) setMeta(metaProp) }, [metaProp])
  const loadMeta = useCallback(async () => {
    try {
      setMeta(await runtime.meta())
      setError('')
    } catch (e) {
      setError(errorText(e, 'Support could not be loaded.'))
    }
  }, [runtime])
  useEffect(() => { if (!metaProp) void loadMeta() }, [metaProp, loadMeta])

  useEffect(() => {
    if (!initialIssue) return
    setBoardIssue(initialIssue)
    setMineIssue(initialIssue)
    setTab('board')
  }, [initialIssue])
  // A link to a tab (a release notification → Releases) moves there.
  useEffect(() => {
    if (initialTab && !initialIssue) setTab(initialTab)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTab])

  const wrap = fill
    ? { height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' as const, padding: '0.9rem 1.1rem', boxSizing: 'border-box' as const }
    : { padding: '0.25rem 0' }

  if (!meta) {
    return (
      <SupportScope style={wrap}>
        <ErrorLine message={error} onRetry={() => { void loadMeta() }} />
        {!error && <div style={{ display: 'flex', gap: 8 }}><Spinner /> Loading support…</div>}
      </SupportScope>
    )
  }

  // Not on the team: their own issues, and the way to report one.
  if (!meta.me.is_team) {
    return (
      <SupportScope style={wrap}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
          <strong style={{ fontSize: '1.0625rem', flex: 1 }}>
            {mineIssue ? `Issue #${mineIssue}` : 'My issues'}
          </strong>
          {!mineIssue && (
            <button type="button" style={btn('primary', true)} onClick={() => setReporting(true)}>
              <Plus size={12} /> Report an issue
            </button>
          )}
        </div>
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {mineIssue ? (
            <IssueDetail runtime={runtime} number={mineIssue} meta={meta} notify={notify}
              onBack={() => { setMineIssue(null); onIssueChange?.(null); setListKey((k) => k + 1) }} />
          ) : (
            <MyIssues runtime={runtime} meta={meta} refreshKey={listKey}
              onOpen={(n) => { setMineIssue(n); onIssueChange?.(n) }} />
          )}
        </div>
        <ReportIssueDialog open={reporting} onClose={() => setReporting(false)} runtime={runtime}
          meta={meta} source={newIssueSource} notify={notify} getContext={getContext}
          onCreated={(i) => { setMineIssue(i.number); onIssueChange?.(i.number) }} />
      </SupportScope>
    )
  }

  const caps = meta.me.capacities
  return (
    <SupportScope style={wrap}>
      <Tabs<SupportTab>
        value={tab}
        onChange={setTab}
        tabs={[
          { id: 'board', label: <><LayoutGrid size={13} style={{ verticalAlign: -2 }} /> Issues</> },
          { id: 'releases', label: <><Rocket size={13} style={{ verticalAlign: -2 }} /> Releases</>, show: !!runtime.listReleases },
          { id: 'versioning', label: <><GitBranch size={13} style={{ verticalAlign: -2 }} /> Versions &amp; deployments</>, show: !!runtime.versioningStatus },
          {
            id: 'settings', label: <><Settings2 size={13} style={{ verticalAlign: -2 }} /> Settings</>,
            show: !!runtime.getPolicy && (caps.includes('pm') || caps.includes('dev')),
          },
        ]}
      />
      <div style={{ flex: 1, minHeight: 0, overflowY: tab === 'board' ? 'hidden' : 'auto' }}>
        {tab === 'board' && (
          <SupportBoard runtime={runtime} meta={meta} notify={notify} onOpenStaging={onOpenStaging}
            initialIssue={boardIssue} getContext={getContext} newIssueSource={newIssueSource}
            onIssueChange={(n) => { setBoardIssue(n); onIssueChange?.(n) }} />
        )}
        {tab === 'releases' && (
          <ReleasesView runtime={runtime} meta={meta} notify={notify}
            onOpenIssue={(n) => { setBoardIssue(n); setTab('board'); onIssueChange?.(n) }} />
        )}
        {tab === 'versioning' && <VersioningPanel runtime={runtime} meta={meta} notify={notify} />}
        {tab === 'settings' && <PolicyEditor runtime={runtime} meta={meta} notify={notify} />}
      </div>
    </SupportScope>
  )
}
